import { useCallback, useState } from "react";

import type { SwitchWorkspaceModalProps } from "../components/SwitchWorkspaceModal";
import { logEventV2 } from "react-migration/lib/util/logEventV2";
import { setSelectedProjectId } from "src/js/stores/site/actions/setSelectedProject";
import { siteStore } from "src/js/stores/site/store";
import { useSnapshot } from "valtio/react";

type WorkspaceSwitchSource = "global_search" | "rhp_intersecting";

type PendingWorkspaceSwitch = {
  workspaceId: string;
  workspaceTitle: string;
  onSuccess: () => void | Promise<void>;
};

export function useWorkspaceSwitchGate({ source }: { source: WorkspaceSwitchSource }) {
  const { selectedProjectId } = useSnapshot(siteStore);
  const [pending, setPending] = useState<PendingWorkspaceSwitch | null>(null);

  const checkWorkspaceAccess = useCallback(
    (workspaceId: string | undefined, workspaceTitle: string, onSuccess: () => void | Promise<void>) => {
      if (workspaceId && selectedProjectId && workspaceId !== selectedProjectId) {
        setPending({ workspaceId, workspaceTitle, onSuccess });
        return;
      }
      onSuccess();
    },
    [selectedProjectId]
  );

  const handleCancel = useCallback(() => {
    const targetId = pending?.workspaceId;
    if (targetId) {
      logEventV2({
        name: "Workspace Switch Modal",
        properties: {
          action: "cancel",
          targetProjectId: targetId,
          source,
        },
      });
    }
    setPending(null);
  }, [pending, source]);

  const handleConfirm = useCallback(() => {
    if (!pending) {
      setPending(null);
      return;
    }

    const { workspaceId, onSuccess } = pending;

    logEventV2({
      name: "Workspace Switch Modal",
      properties: {
        action: "confirm_switch",
        targetProjectId: workspaceId,
        source,
      },
    });
    setSelectedProjectId(workspaceId);
    setPending(null);
    onSuccess();
  }, [pending, source]);

  const modalProps: SwitchWorkspaceModalProps = {
    open: !!pending,
    workspaceTitle: pending?.workspaceTitle ?? "",
    onCancel: handleCancel,
    onConfirm: handleConfirm,
  };

  return { checkWorkspaceAccess, modalProps };
}
